import React from 'react';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Badge,
  BadgeText,
  Button,
  ButtonText,
  ScrollView,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

const PreflightCheckInModal = ({
  isOpen,
  onClose,
  bookings,
  defaultSelectedBookingIds,
  onConfirm,
  isSubmitting,
  isReopen = false,
}) => {
  const { t } = useTranslation();
  const [selectedBookingIds, setSelectedBookingIds] = React.useState(defaultSelectedBookingIds || []);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedBookingIds(defaultSelectedBookingIds || []);
    }
    wasOpenRef.current = isOpen;
  }, [defaultSelectedBookingIds, isOpen]);

  const selectedIdSet = React.useMemo(() => new Set(selectedBookingIds), [selectedBookingIds]);

  const toggleSelection = (bookingId) => {
    setSelectedBookingIds((prev) => (prev.includes(bookingId)
      ? prev.filter((id) => id !== bookingId)
      : [...prev, bookingId]));
  };

  const startLabel = isReopen
    ? t('lecturer.eventCalendar.sidebar.preflightReopenQuiz', 'Reopen Quiz')
    : t('lecturer.eventCalendar.sidebar.preflightStartQuiz', 'Start Quiz');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <VStack space="xs" flex={1}>
            <Text bold>
              {t('lecturer.eventCalendar.sidebar.preflightModalTitle', 'Pre-flight Check-in')}
            </Text>
            <Text size="xs" color="$textLight600">
              {t(
                'lecturer.eventCalendar.sidebar.preflightModalDescription',
                'Select students who may access the quiz in this session.',
              )}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody>
          {bookings.length === 0 ? (
            <Text size="sm" color="$textLight500">
              {t('lecturer.eventCalendar.sidebar.preflightNoRegistrations', 'No active registrations for this slot.')}
            </Text>
          ) : (
            <VStack space="sm">
              <Badge action="muted" variant="outline" alignSelf="flex-start">
                <BadgeText size="xs">
                  {t('lecturer.eventCalendar.sidebar.preflightSelectedCount', {
                    defaultValue: '{{count}} selected',
                    count: selectedBookingIds.length,
                  })}
                </BadgeText>
              </Badge>
              <ScrollView style={{ maxHeight: 260 }}>
                <VStack space="sm" py="$1">
                  {bookings.map((booking) => {
                    const isSelected = selectedIdSet.has(booking.bookingId);
                    return (
                      <HStack key={booking.bookingId} alignItems="center" justifyContent="space-between" space="sm">
                        <VStack flex={1}>
                          <Text size="sm">{booking.studentName}</Text>
                          <Text size="xs" color="$textLight500">
                            {booking.status}
                          </Text>
                        </VStack>
                        <Button
                          testID={`preflight-toggle-${booking.bookingId}`}
                          size="xs"
                          variant={isSelected ? 'solid' : 'outline'}
                          action={isSelected ? 'success' : 'secondary'}
                          onPress={() => toggleSelection(booking.bookingId)}
                          isDisabled={isSubmitting}
                        >
                          <ButtonText size="xs">
                            {isSelected
                              ? t('lecturer.eventCalendar.sidebar.revoke', 'Revoke')
                              : t('lecturer.eventCalendar.sidebar.admit', 'Admit')}
                          </ButtonText>
                        </Button>
                      </HStack>
                    );
                  })}
                </VStack>
              </ScrollView>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack space="sm">
            <Button variant="outline" size="sm" onPress={onClose} isDisabled={isSubmitting}>
              <ButtonText>{t('common.buttons.cancel', 'Cancel')}</ButtonText>
            </Button>
            <Button
              testID="preflight-confirm-button"
              size="sm"
              onPress={() => onConfirm(selectedBookingIds)}
              isDisabled={isSubmitting || bookings.length === 0}
            >
              <ButtonText>{isSubmitting ? t('common.status.saving', 'Saving...') : startLabel}</ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreflightCheckInModal;
